import { Loader2 } from "lucide-react";
import { clockLabel, elapsedSec, useNowTick } from "@/lib/useNowTick";
import { cn } from "@/lib/utils";

export type StillAiJobState = {
  startedAt: string;
  expectedMs: number;
  samples: number;
  geminiCalls: number;
  labels: string[];
};

export default function StillAiJobProgress({
  job,
  running,
  className,
}: {
  job: StillAiJobState | null;
  running: boolean;
  className?: string;
}) {
  const active = Boolean(running && job?.startedAt);
  const now = useNowTick(active);
  if (!job?.startedAt) return null;

  const expectedMs = Math.max(3_000, Number(job.expectedMs) || 12_000);
  const elapsed = elapsedSec(job.startedAt, now);
  const etaSec = active ? Math.max(0, Math.round(expectedMs / 1000) - elapsed) : 0;
  const expectedSec = Math.max(1, Math.round(expectedMs / 1000));
  const samples = Number(job.samples || 0);
  const timePct = Math.min(99, Math.max(4, Math.round((elapsed * 1000 * 100) / expectedMs)));
  const pct = running ? timePct : 100;
  const labels = job.labels?.length ? job.labels : ["AI"];
  const title = running
    ? job.geminiCalls > 0
      ? `AI obrabia klatkę (${labels.slice(1).join(" → ") || "Gemini"})`
      : "Zapisuję klatkę…"
    : "Gotowe";

  return (
    <div
      className={cn("rounded-md border border-border bg-muted/30 p-3", className)}
      aria-live="polite"
      role="status"
    >
      <div className="mb-1.5 flex items-center justify-between gap-2 text-xs">
        <span className="flex min-w-0 items-center gap-1.5 font-medium">
          {running ? <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-brand" /> : null}
          <span className="truncate">{title}</span>
        </span>
        <span className="tabular-nums text-muted-foreground">{pct}%</span>
      </div>
      <div
        className="h-2 overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
      >
        <div className="h-full bg-brand transition-all" style={{ width: `${pct}%` }} />
      </div>
      {labels.length > 1 ? (
        <div className="mt-1 flex gap-0.5 text-[10px] text-muted-foreground">
          {labels.map((label) => (
            <span key={label} className="min-w-0 flex-1 truncate">
              {label}
            </span>
          ))}
        </div>
      ) : null}
      <p className="mt-2 text-xs text-muted-foreground">
        minęło {clockLabel(elapsed)}
        {active ? ` · zostało ok. ${clockLabel(etaSec)}` : ""}
        {active ? ` · średnio ~${clockLabel(expectedSec)}` : ""}
        {samples > 0
          ? ` · średnia z ${samples} ${samples === 1 ? "generacji" : "generacji tego typu"}`
          : ""}
      </p>
    </div>
  );
}
